import { inject } from '@angular/core';
import { map } from 'rxjs';
import { signalStore } from '@ngrx/signals';
import { withEntities } from '@ngrx/signals/entities';
import { withCursorPagination } from '@dgacontrol-frontend/shared-util-cursor-pagination';

import { BillingWorkChargeLineService } from './generated/billing-work-charge-line/billing-work-charge-line.service';
import { WorkChargesStore } from './work-charges.store';
import { withChargeLines } from './charge-lines.feature';
import type {
  PatchedWorkChargeLineUpdateRequestV1Request,
  WorkChargeLineCreateRequestV1Request,
  WorkChargeLineV1,
} from './generated/types';

/**
 * Lines of ONE work charge. COMPONENT-SCOPED: goes in the lines modal's `providers`, so a
 * fresh instance is created per opening and the modal can never flash the previous charge's
 * lines. Precedent: `dt-downlink-request-attempts` (a downlink request's send attempts).
 *
 * The parent charge stopped shipping `lines[]` (M-bill-4): they are their own resource now,
 * so this store owns ALL line I/O — the listing, and the three mutations through
 * `withChargeLines`, shared with the other line stores.
 *
 * The `Query` of the cursor feature is the PARENT identity: `load(chargeIdentity)`. Reading
 * it from `DynamicDialogConfig` instead would drag PrimeNG into the data layer and force
 * every test to mount the modal.
 *
 * ponytail: `loadMore` comes free with the feature and has no caller — an SII invoice caps
 * at 16 items, so one page always covers a charge.
 */
export const WorkChargeLinesStore = signalStore(
  withEntities<WorkChargeLineV1>(),
  withCursorPagination<WorkChargeLineV1, string>(() => {
    const api = inject(BillingWorkChargeLineService);
    // The generated response IS `CursorPage<WorkChargeLineV1>` ({results,next,previous}).
    return (identity, cursor) =>
      api.billingV1WorkChargeLinesList(
        { identity },
        { cursor: cursor ?? undefined },
      );
  }),
  withChargeLines(() => {
    const api = inject(BillingWorkChargeLineService);
    const charges = inject(WorkChargesStore);
    return {
      create: (identity, body: WorkChargeLineCreateRequestV1Request) =>
        api
          .billingV1WorkChargeLinesCreate({ identity }, body)
          .pipe(map((res) => res.work_charge)),
      update: (
        identity,
        lineIdentity,
        body: PatchedWorkChargeLineUpdateRequestV1Request,
      ) =>
        api
          .billingV1WorkChargeLinesPartialUpdate({ identity, lineIdentity }, body)
          .pipe(map((res) => res.work_charge)),
      destroy: (identity, lineIdentity) =>
        api.billingV1WorkChargeLinesDestroy({ identity, lineIdentity }),
      // Called late, never bound at factory time: the parent store's methods
      // are looked up on each use, as the inline version did.
      syncParent: (charge) => {
        charges.syncCharge(charge);
      },
      refreshParent: (identity) => charges.refreshCharge(identity),
    };
  }),
);
